import { config } from "../config"
import axios, { type AxiosInstance } from "axios";
import { z } from "zod";

import SessionStore from "../stores/SessionStore";
import { ApiResponseSchema, type ApiResponseType } from "../entities/apiResponse/apiResponse.schema";
import type { Prettify } from "../utils/typeUtils";
// import { redirectToLoginWithMessage } from "../utils/navigationUtils";

export interface ApiErrorResponse {
  status: "error";
  message?: string;
  retryable?: boolean;
  retry_after?: number;
}
export interface ApiSuccessResponse {
  status: "success";
}

export type ApiBaseResponse = Prettify<ApiErrorResponse | ApiSuccessResponse>;

export class ApiRetryableError extends Error {
  readonly retryable: boolean;
  readonly retryAfter: number | null;
  readonly statusCode: number | null;

  constructor(opts: { message: string; retryable?: boolean; retryAfter?: number | null; statusCode?: number | null }) {
    super(opts.message);
    this.name = 'ApiRetryableError';
    this.retryable = opts.retryable ?? false;
    this.retryAfter = opts.retryAfter ?? null;
    this.statusCode = opts.statusCode ?? null;
  }
}


const REFRESH_INTERVAL_MS = 9 * 60 * 1000; // 9 minutes

export class ApiService {
  private apiClient: AxiosInstance;
  private useCredentials: boolean = true;
  private lastRefreshTime: number | null = null;
  private refreshInProgress: Promise<void> | null = null;
  private autoRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private isHydrating: boolean = false;

  constructor() {
    this.apiClient = axios.create({
      //baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
      baseURL: config.API_BASE_URL || "/api",
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      }
    });

    this.apiClient.interceptors.request.use((config) => {
      if (this.useCredentials) {
        config.withCredentials = true;
      }
      return config;
    });


    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const isRefreshRequest = String(error.config?.url || "").includes("refreshCookie");
          this.showSessionExpiredIfNeeded(isRefreshRequest ? { force: true } : undefined);
        }
        return Promise.reject(error);
      },
    );
  }

  setUseCredentials(useCredentials: boolean) {
    this.useCredentials = useCredentials;
  }

  setHydrating(hydrating: boolean): void {
    this.isHydrating = hydrating;
  }

  /**
   * Refreshes the session using the HttpOnly refresh cookie.
   * Updates lastRefreshTime only on success.
   */
  async refreshCookie(): Promise<void> {
    this.setUseCredentials(true);
    await this.apiClient.post("authenticate/refreshCookie", {});
    SessionStore.useSessionStore.getState().markAuthOk();
    this.lastRefreshTime = Date.now();
    console.log("refreshed cookie", this.lastRefreshTime?.toString(), "seconds");
  }

  markSessionEstablished(): void {
    this.lastRefreshTime = Date.now();
    this.startAutoRefresh();
  }

  clearSession(): void {
    this.lastRefreshTime = null;
    this.stopAutoRefresh();
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.autoRefreshTimer = setInterval(() => {
      if (this.lastRefreshTime === null) {
        this.stopAutoRefresh();
        return;
      }
      if (this.refreshInProgress) return;

      this.refreshInProgress = this.refreshCookie()
        .catch(() => {
          this.stopAutoRefresh();
          this.lastRefreshTime = null;
          this.showSessionExpiredIfNeeded({ force: true });
        })
        .finally(() => {
          this.refreshInProgress = null;
        });
    }, REFRESH_INTERVAL_MS);
  }

  private stopAutoRefresh(): void {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  private async ensureRefreshedIfNeeded(endpoint: string): Promise<void> {
    const skipUrls = ["refreshCookie", "signin", "signout"];
    if (!this.useCredentials || skipUrls.some((u) => endpoint.includes(u))) return;
    if (this.lastRefreshTime === null) return;

    const now = Date.now();
    if (now - this.lastRefreshTime <= REFRESH_INTERVAL_MS) return;

    if (this.refreshInProgress) {
      await this.refreshInProgress;
      return;
    }

    const doRefresh = async () => {
      try {
        await this.refreshCookie();
      } catch {
        this.showSessionExpiredIfNeeded({ force: true });
        this.lastRefreshTime = null;
        throw new Error("Session expired");
      } finally {
        this.refreshInProgress = null;
      }
    };

    this.refreshInProgress = doRefresh();
    await this.refreshInProgress;
  }

  async getParsed<T extends Record<string, z.ZodType>>(endPoint: string, responseDataSchema: T, config?: object): Promise<ApiResponseType<T>> {
    try {
      await this.ensureRefreshedIfNeeded(endPoint);
      const response = await this.apiClient.get(endPoint, config);
      return ApiResponseSchema(responseDataSchema).parse(response.data);

    } catch (error) {
      return this.handleError(error);
    }
  }

  async get<T>(endpoint: string, config?: object): Promise<T> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.get<T>(endpoint, config);
      return response.data;

    } catch (error) {
      return this.handleError(error);
    }
  }


  async postParsed<T extends Record<string, z.ZodType>>(endpoint: string, bodyData: unknown, responseDataSchema: T, config?: object): Promise<ApiResponseType<T>> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.post(endpoint, bodyData, config);
      return ApiResponseSchema(responseDataSchema).parse(response.data);

    } catch (error) {
      return this.handleError(error);
    }
  }

  async post<T>(endpoint: string, data?: unknown, config?: object): Promise<T> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.post<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async putParsed<T extends Record<string, z.ZodType>>(endpoint: string, bodyData: unknown, responseDataSchema: T, config?: object): Promise<ApiResponseType<T>> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.put(endpoint, bodyData, config);
      return ApiResponseSchema(responseDataSchema).parse(response.data);

    } catch (error) {
      return this.handleError(error);
    }
  }

  async put<T>(endpoint: string, data?: unknown, config?: object): Promise<T> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.put<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }


  async patchParsed<T extends Record<string, z.ZodType>>(endpoint: string, bodyData: unknown, responseDataSchema: T, config?: object): Promise<ApiResponseType<T>> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.patch(endpoint, bodyData, config);
      return ApiResponseSchema(responseDataSchema).parse(response.data);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async patch<T>(endpoint: string, data?: unknown, config?: object): Promise<T> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.patch<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteParsed<T extends Record<string, z.ZodType>>(endpoint: string, responseDataSchema: T, config?: object): Promise<ApiResponseType<T>> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.delete(endpoint, config);
      return ApiResponseSchema(responseDataSchema).parse(response.data);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async delete<T>(endpoint: string, config?: object): Promise<T> {
    try {
      await this.ensureRefreshedIfNeeded(endpoint);
      const response = await this.apiClient.delete<T>(endpoint, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  private showSessionExpiredIfNeeded(options?: { force?: boolean }): void {
    if (this.isHydrating) return;
    const currentPath = window.location.pathname;
    if (currentPath === "/login") return;

    const session = SessionStore.useSessionStore.getState();
    if (session.isSessionExpiredModalOpen) return;

    const shouldShow = options?.force === true || session.shouldShowSessionExpiredModal();
    if (shouldShow) {
      session.showSessionExpiredModal();
    }
  }

  private handleError(error: unknown): never {
    if (axios.isAxiosError(error)) {
      if (error.response) {

        const contentType: string = error.response.headers?.['content-type'] ?? '';
        if (!contentType.includes('application/json') && error.response.status >= 500) {
          throw new ApiRetryableError({
            message: 'Network error. Please check your connection.',
            retryable: false,
            retryAfter: null,
            statusCode: null,
          });
        }

        const body = error.response.data as Record<string, unknown> | null;
        const retryable = typeof body?.retryable === 'boolean' ? (body.retryable as boolean) : false;
        const retryAfter = typeof body?.retry_after === 'number' ? (body.retry_after as number) : null;
        const status = error.response.status;
        const message = typeof body?.message === 'string' && body.message
          ? body.message
          : status === 504
            ? 'The AI took too long to respond. Please try again.'
            : status >= 500
              ? 'Something went wrong on our end. Please try again.'
              : 'An error occurred while processing the request.';
        throw new ApiRetryableError({ message, retryable, retryAfter, statusCode: status });
      }
      // No response - network error or timeout
      throw new ApiRetryableError({
        message: error.message || 'Network error. Please check your connection.',
        retryable: false,
        retryAfter: null,
        statusCode: null,
      });
    }
    throw error;
  }

}


let _apiService: ApiService | null = null;

export function getApiService(): ApiService {
  if (!_apiService) {
    _apiService = new ApiService();
    return _apiService;
  }
  return _apiService;
}


export function setApiService(apiService: ApiService): void {
  _apiService = apiService;
}


